import { Box, Grid, Typography } from "@mui/material";
import LinearProgress from "@mui/material/LinearProgress";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import BriaButton from "../../../../../components/common/BriaButton/BriaButton";
import BriaDropdown, { DropDownItem } from "../../../../../components/common/BriaDropDown/BriaDropDown";
import BriaInput from "../../../../../components/common/BriaInput/BriaInput";
import BriaSlider from "../../../../../components/common/BriaSlider/BriaSlider";
import ConfirmationPopup from "../../../../../components/common/ConfirmationPopup/ConfirmationPopup.tsx";
import LoadingPlaceholder from "../../../../../components/common/LoadingPlaceholder/LoadingPlaceholder";
import { useAppStore } from "../../../../../hooks/useStores";
import InputLayout from "../../../../../layout/InputLayout/InputLayout";
import { formatScientific } from "../../../../../utils";
import {
	CheckpointInterval,
	LEARNING_RATE_CONFIG,
	LEARNING_RATE_VALUES,
	LR_WARMUP_STEPS_CONFIG,
	Rank,
	TOTAL_TRAINING_STEPS_CONFIG,
	TgModelLearningRateScheduler,
	TgModelParameters,
	TrainingMode,
} from "../../store/tg-model-store";
import styles from "./NewTgExpertParametersSetup.module.scss";
import { TrainingModeSelector } from "./TrainingModeSelector/TrainingModeSelector";

interface NewTgExpertParametersSetupProps {
	isVisitedImagesCaptioning: boolean;
	onBackToVisualDna?: () => void;
	handleCreateModelAndStartTraining: () => void;
	handleStartTrainingOnly: () => Promise<void>;
	handleNavigateAfterTraining: () => void;
	loading?: boolean;
	datasetSize?: number;
}

const NewTgExpertParametersSetup = ({
	isVisitedImagesCaptioning,
	onBackToVisualDna,
	handleCreateModelAndStartTraining,
	handleStartTrainingOnly,
	handleNavigateAfterTraining,
	loading = false,
	datasetSize,
}: NewTgExpertParametersSetupProps) => {
	const { t } = useTranslation("tg", { keyPrefix: "models.create.newExpertParameters" });
	const { t: t2 } = useTranslation("tg", { keyPrefix: "models.parametersLrScheduler" });
	const { tgStore } = useAppStore();
	const { tgModelStore } = tgStore;
	const { tgDatasetStore } = tgStore;

	// Form state
	const [modelName, setModelName] = useState("");
	const [modelDescription, setModelDescription] = useState("");
	const [rank, setRank] = useState<Rank>(Rank.RANK_32);
	const snapLearningRate = (value: number) => {
		return LEARNING_RATE_VALUES.reduce(
			(prev, curr) => (Math.abs(curr - value) < Math.abs(prev - value) ? curr : prev),
			LEARNING_RATE_VALUES[0],
		);
	};
	const [learningRate, setLearningRate] = useState<number>(LEARNING_RATE_VALUES[2]);
	const [learningRateScheduler, setLearningRateScheduler] = useState<TgModelLearningRateScheduler>(
		TgModelLearningRateScheduler.CONSTANT_WITH_WARMUP,
	);
	const [checkpointInterval, setCheckpointInterval] = useState<CheckpointInterval | undefined>(undefined);
	const [totalTrainingSteps, setTotalTrainingSteps] = useState<number>(500);
	const [lrWarmupSteps, setLrWarmupSteps] = useState<number>(LR_WARMUP_STEPS_CONFIG.default);

	const [loadingDefaults, setLoadingDefaults] = useState(true);
	const [trainingMode, setTrainingMode] = useState<TrainingMode>(
		tgDatasetStore.datasetForm.upload_type === "advanced" ? TrainingMode.EXPERT : TrainingMode.FULLY_AUTOMATED,
	);
	const [showGenerationPopup, setShowGenerationPopup] = useState(false);
	const [pendingStartAfterRegenerate, setPendingStartAfterRegenerate] = useState(false);
	const [showTrainingStartedPopup, setShowTrainingStartedPopup] = useState(false);

	useEffect(() => {
		const loadDefaults = async () => {
			if (tgModelStore.modelForm.training_parameters != undefined) {
				// Incase of duplicate action clicked, these values will be prefilled.
				tgModelStore.modelForm.training_version = "fibo";
				setRank(tgModelStore.modelForm.training_parameters.rank);
				setLearningRate(snapLearningRate(tgModelStore.modelForm.training_parameters.learning_rate));
				setTotalTrainingSteps(tgModelStore.modelForm.training_parameters.total_training_steps);
				setLearningRateScheduler(
					tgModelStore.modelForm.training_parameters.lr_scheduler as TgModelLearningRateScheduler,
				);
				setLrWarmupSteps(
					tgModelStore.modelForm.training_parameters?.lr_warmup_steps ?? LR_WARMUP_STEPS_CONFIG.default,
				);
				setCheckpointInterval(tgModelStore.modelForm.training_parameters.checkpoint_interval);
				setModelName(tgModelStore.modelForm.name);
				setModelDescription(tgModelStore.modelForm.description);
				setLoadingDefaults(false);
				return;
			}

			try {
				setLoadingDefaults(true);
				const { ip_medium, ip_type } = tgStore.tgProjectStore.projectToView;
				if (!ip_medium || !ip_type) return;
				await tgModelStore.fetchDefaultExpertParameters({
					ip_medium,
					ip_type,
					dataset_size: datasetSize ?? 0,
				});
				const d = tgModelStore.defaultExpertParameters;
				if (d) {
					setRank(d.rank);
					setLearningRate(snapLearningRate(d.learning_rate));
					setTotalTrainingSteps(d.total_training_steps);
					setLearningRateScheduler(
						(d.lr_scheduler as TgModelLearningRateScheduler) ??
							TgModelLearningRateScheduler.CONSTANT_WITH_WARMUP,
					);
					if (d.lr_warmup_steps != null) {
						setLrWarmupSteps(d.lr_warmup_steps);
					} else {
						setLrWarmupSteps(LR_WARMUP_STEPS_CONFIG.default);
					}
				}
			} finally {
				setLoadingDefaults(false);
			}
		};
		loadDefaults();
	}, [datasetSize]);

	const rankItems: DropDownItem<Rank>[] = [
		{ key: Rank.RANK_4.toString(), value: Rank.RANK_4 },
		{ key: Rank.RANK_8.toString(), value: Rank.RANK_8 },
		{ key: Rank.RANK_16.toString(), value: Rank.RANK_16 },
		{ key: Rank.RANK_32.toString(), value: Rank.RANK_32 },
		{ key: Rank.RANK_64.toString(), value: Rank.RANK_64 },
		{ key: Rank.RANK_128.toString(), value: Rank.RANK_128 },
		{ key: Rank.RANK_256.toString(), value: Rank.RANK_256 },
	];

	const learningRateSchedulerItems: DropDownItem<TgModelLearningRateScheduler>[] = [
		{ key: t2("linear"), value: TgModelLearningRateScheduler.LINEAR },
		{ key: t2("cosine"), value: TgModelLearningRateScheduler.COSINE },
		{ key: t2("cosine_with_restarts"), value: TgModelLearningRateScheduler.COSINE_WITH_RESTARTS },
		{ key: t2("polynomial"), value: TgModelLearningRateScheduler.POLYNOMIAL },
		{ key: t2("constant"), value: TgModelLearningRateScheduler.CONSTANT },
		{ key: t2("constant_with_warmup"), value: TgModelLearningRateScheduler.CONSTANT_WITH_WARMUP },
	];

	// Filter checkpoint interval options based on total training steps
	const getCheckpointIntervalItems = (): DropDownItem<CheckpointInterval>[] => {
		const intervals = [
			CheckpointInterval.INTERVAL_100,
			CheckpointInterval.INTERVAL_200,
			CheckpointInterval.INTERVAL_500,
			CheckpointInterval.INTERVAL_1000,
			CheckpointInterval.INTERVAL_1500,
		];

		return intervals
			.filter((interval) => interval <= totalTrainingSteps)
			.map((interval) => ({
				key: interval.toString(),
				value: interval,
			}));
	};

	// Update checkpoint interval if current value is larger than total training steps
	useEffect(() => {
		if (!checkpointInterval) return;
		const availableIntervals = getCheckpointIntervalItems();
		if (checkpointInterval > totalTrainingSteps && availableIntervals.length > 0) {
			setCheckpointInterval(availableIntervals[availableIntervals.length - 1].value);
		}
	}, [totalTrainingSteps, checkpointInterval]);

	// Update model form when parameters change
	useEffect(() => {
		tgModelStore.handleFormChange("name", modelName);
		tgModelStore.handleFormChange("description", modelDescription);
		tgModelStore.handleFormChange("training_mode", trainingMode);

		if (trainingMode === TrainingMode.EXPERT) {
			const parameters: TgModelParameters = {
				rank,
				checkpoint_interval: checkpointInterval,
				lr_scheduler: learningRateScheduler,
				learning_rate: learningRate,
				total_training_steps: totalTrainingSteps,
				...(learningRateScheduler === TgModelLearningRateScheduler.CONSTANT_WITH_WARMUP
					? { lr_warmup_steps: lrWarmupSteps }
					: {}),
			};
			tgModelStore.handleFormChange("training_parameters", parameters);
		} else {
			tgModelStore.handleFormChange("training_parameters", undefined);
		}
	}, [
		modelName,
		modelDescription,
		trainingMode,
		rank,
		learningRate,
		learningRateScheduler,
		checkpointInterval,
		totalTrainingSteps,
		lrWarmupSteps,
		tgModelStore,
	]);

	useEffect(() => {
		if (learningRateScheduler !== TgModelLearningRateScheduler.CONSTANT_WITH_WARMUP) {
			setLrWarmupSteps(LR_WARMUP_STEPS_CONFIG.default);
		}
	}, [learningRateScheduler]);

	const handleStartTraining = async () => {
		if (
			!isVisitedImagesCaptioning &&
			tgDatasetStore.datasetForm?.id &&
			tgDatasetStore.datasetForm.upload_type !== "advanced" &&
			!tgModelStore.isDatasetFromExisting
		) {
			try {
				tgDatasetStore
					.regenerateAllCaptions(
						tgDatasetStore.datasetForm.id,
						undefined,
						(newDataset: any) => {
							tgDatasetStore.handleFormChange("images", newDataset.images);
							tgDatasetStore.handleFormChange(
								"captions_update_status" as any,
								newDataset.captions_update_status as any,
							);
						},
						300,
						20,
					)
					.catch(() => {});
				setShowGenerationPopup(true);
				setPendingStartAfterRegenerate(true);
				return;
			} catch {
				// ignore
			}
		}
		if (tgDatasetStore.loadingRegenerate) {
			setShowGenerationPopup(true);
			setPendingStartAfterRegenerate(true);
			return;
		}
		await handleStartTrainingOnly();
		setShowTrainingStartedPopup(true);
	};

	useEffect(() => {
		if (pendingStartAfterRegenerate && !tgDatasetStore.loadingRegenerate) {
			handleCreateModelAndStartTraining();
			setShowGenerationPopup(false);
			setPendingStartAfterRegenerate(false);
		}
	}, [pendingStartAfterRegenerate, tgDatasetStore.loadingRegenerate]);

	const isFormValid = modelName.trim().length > 0;

	const generateMarks = (start: number, end: number, totalPoints: number = 6) => {
		const step = (end - start) / (totalPoints - 1);
		const marks = [];

		for (let i = 0; i < totalPoints; i++) {
			const value = Math.round(start + step * i);
			marks.push({
				value,
				label: i === 0 || i === totalPoints - 1 ? String(value) : "",
			});
		}

		return marks;
	};

	return (
		<LoadingPlaceholder isLoading={loadingDefaults} className={styles.loadingPlaceholder}>
			<Box className={styles.container}>
				<Typography className={styles.subtitle}>{t("subtitle")}</Typography>
				<TrainingModeSelector
					value={trainingMode}
					onChange={setTrainingMode}
					disabledFullyAutomated={tgDatasetStore.datasetForm.upload_type === "advanced"}
				/>

				<Box className={styles.formContainer}>
					<Grid container spacing={3} className={styles.formGrid}>
						<Grid item xs={12} md={3}>
							<InputLayout
								label={t("modelName.label")}
								info={t("modelName.tooltip")}
								className={styles.inputLayout}
							>
								<BriaInput
									value={modelName}
									onChange={(e) => setModelName(e.target.value)}
									placeholder={t("modelName.placeholder")}
									className={styles.input}
								/>
							</InputLayout>
						</Grid>
						<Grid item xs={12} md={9}>
							<InputLayout label={t("modelDescription.label")} className={styles.inputLayout}>
								<BriaInput
									value={modelDescription}
									onChange={(e) => setModelDescription(e.target.value)}
									placeholder={t("modelDescription.placeholder")}
									className={styles.input}
								/>
							</InputLayout>
						</Grid>

						{trainingMode === TrainingMode.EXPERT && (
							<>
								<Grid item xs={12} md={4}>
									<InputLayout
										label={t("learningRate.label")}
										info={t("learningRate.tooltip")}
										className={styles.inputLayout}
									>
										<BriaSlider
											value={learningRate}
											onChange={(_, value) => setLearningRate(snapLearningRate(value as number))}
											min={LEARNING_RATE_CONFIG.min}
											max={LEARNING_RATE_CONFIG.max}
											step={null}
											className={styles.slider}
											valueLabelFormat={(v) => formatScientific(v as number)}
											showOnlyEdgeMarkLabels
											valueLabelDisplay={"auto"}
											showMarksPoints
											labelsAlwaysGray
											logScale
											logBase={10}
											marks={LEARNING_RATE_VALUES.map((v) => ({
												value: v,
												label: formatScientific(v),
											}))}
										/>
									</InputLayout>
								</Grid>
								<Grid item xs={12} md={4}>
									<InputLayout
										label={t("rank.label")}
										info={t("rank.tooltip")}
										className={styles.inputLayout}
									>
										<BriaDropdown
											value={rank}
											onChange={(e) => setRank(e.target.value as Rank)}
											items={rankItems}
										/>
									</InputLayout>
								</Grid>
								<Grid item xs={12} md={4}>
									<InputLayout
										label={t("learningRateScheduler.label")}
										info={t("learningRateScheduler.tooltip")}
										className={styles.inputLayout}
									>
										<BriaDropdown
											value={learningRateScheduler}
											onChange={(e) =>
												setLearningRateScheduler(e.target.value as TgModelLearningRateScheduler)
											}
											items={learningRateSchedulerItems}
										/>
									</InputLayout>
								</Grid>

								<Grid item xs={12} md={4}>
									<InputLayout
										label={t("totalTrainingSteps.label")}
										info={t("totalTrainingSteps.tooltip")}
										className={styles.inputLayout}
									>
										<BriaSlider
											value={totalTrainingSteps}
											onChange={(_, value) => setTotalTrainingSteps(value as number)}
											min={TOTAL_TRAINING_STEPS_CONFIG.min}
											max={TOTAL_TRAINING_STEPS_CONFIG.max}
											step={TOTAL_TRAINING_STEPS_CONFIG.step}
											className={styles.slider}
											valueLabelDisplay={"on"}
											showMarksPoints
											labelsAlwaysGray
											marks={generateMarks(
												TOTAL_TRAINING_STEPS_CONFIG.min,
												TOTAL_TRAINING_STEPS_CONFIG.max,
											)}
										/>
									</InputLayout>
								</Grid>
								<Grid item xs={12} md={4}>
									<InputLayout
										label={t("checkpointInterval.label")}
										info={t("checkpointInterval.tooltip")}
										className={styles.inputLayout}
									>
										<BriaDropdown
											value={checkpointInterval}
											onChange={(e) =>
												setCheckpointInterval(e.target.value as CheckpointInterval)
											}
											items={getCheckpointIntervalItems()}
											placeholder={t("checkpointInterval.placeholder")}
											renderValue={(selected) => {
												if (!selected) return t("checkpointInterval.placeholder");
												return selected;
											}}
										/>
									</InputLayout>
								</Grid>
								{learningRateScheduler === TgModelLearningRateScheduler.CONSTANT_WITH_WARMUP && (
									<Grid item xs={12} md={4}>
										<InputLayout
											label={t("lrWarmupSteps.label")}
											info={t("lrWarmupSteps.tooltip")}
											className={styles.inputLayout}
										>
											<BriaSlider
												value={lrWarmupSteps}
												onChange={(_, value) => setLrWarmupSteps(value as number)}
												min={LR_WARMUP_STEPS_CONFIG.min}
												max={LR_WARMUP_STEPS_CONFIG.max}
												step={LR_WARMUP_STEPS_CONFIG.step}
												className={styles.slider}
												showOnlyEdgeMarkLabels
												valueLabelDisplay={"auto"}
												showMarksPoints
												labelsAlwaysGray
												marks={generateMarks(
													LR_WARMUP_STEPS_CONFIG.min,
													LR_WARMUP_STEPS_CONFIG.max,
													11,
												)}
											/>
										</InputLayout>
									</Grid>
								)}
							</>
						)}
					</Grid>
				</Box>

				<Box className={`${styles.buttonContainer} ${styles.fixedFooter}`}>
					{onBackToVisualDna &&
						tgDatasetStore.datasetForm.upload_type !== "advanced" &&
						!tgModelStore.isDatasetFromExisting && (
							<BriaButton
								buttonType="secondarySmall"
								onClick={onBackToVisualDna}
								className={styles.trainButton}
							>
								{t("backToVisualDnaButton")}
							</BriaButton>
						)}
					<BriaButton
						buttonType="primarySmall"
						onClick={handleStartTraining}
						disabled={!isFormValid || loading}
						loading={loading}
						className={styles.trainButton}
					>
						{t("startTrainingButton")}
					</BriaButton>
				</Box>
			</Box>

			<ConfirmationPopup
				open={showGenerationPopup}
				onClose={tgDatasetStore.loadingRegenerate ? () => {} : () => setShowGenerationPopup(false)}
				title={`${t("aboutToStartPopup.title")} ${t("aboutToStartPopup.keepTabNote")}`}
				description={t("aboutToStartPopup.description")}
				hideActions={tgDatasetStore.loadingRegenerate}
				hideCancel={true}
				hideCloseIcon={tgDatasetStore.loadingRegenerate}
			>
				{tgDatasetStore.loadingRegenerate && (
					<Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
						<Typography>
							{t("aboutToStartPopup.progress", {
								loaded:
									(tgDatasetStore.datasetForm.images || []).filter((img: any) => !!img?.caption)
										.length || 0,
								total: tgDatasetStore.datasetForm.images?.length || 0,
							})}
						</Typography>
						<LinearProgress
							variant="determinate"
							value={
								(tgDatasetStore.datasetForm.images?.length || 0) > 0
									? ((tgDatasetStore.datasetForm.images || []).filter((img: any) => !!img?.caption)
											.length /
											tgDatasetStore.datasetForm.images.length) *
										100
									: 0
							}
						/>
					</Box>
				)}
			</ConfirmationPopup>

			<ConfirmationPopup
				open={showTrainingStartedPopup}
				onClose={() => {
					setShowTrainingStartedPopup(false);
					handleNavigateAfterTraining();
				}}
				title={t("startedPopup.title")}
				description={t("startedPopup.description")}
				confirmButtonText={t("startedPopup.button")}
				hideCancel={true}
				onClick={() => {
					setShowTrainingStartedPopup(false);
					handleNavigateAfterTraining();
				}}
			/>
		</LoadingPlaceholder>
	);
};

export default observer(NewTgExpertParametersSetup);
